import React from 'react';
import {Box, Paper, IconButton, Tooltip, CircularProgress} from "@mui/material";
import {ArrowBack as ArrowIcon} from "@mui/icons-material";
import {colors, borders, shadows} from "../ui/colors";
import {t} from "../dashboard/coach/coachTokens";

const ChartCard = ({
                       title,
                       subtitle = null,
                       icon: Icon,
                       children,
                       loading = false,
                       error = null,
                       emptyMessage = "אין נתונים זמינים",
                       onNavigate = null,
                       navigationTooltip = "עבור לעמוד מלא",
                       actionButton = null,
                       headerAction = null,
                       contentPadding = "32px 24px",
                       showData = true,
                       flexBasis = "50%",
                       variant = "default",
                   }) => {
    const coach = variant === "coach";
    return (
        <Box sx={{flex: {xs: "1", md: `0 1 ${flexBasis}`}, display: "flex"}}>
            <Paper
                sx={{
                    backgroundColor: coach ? t.white : colors.white,
                    border: coach ? `1px solid ${t.border}` : borders.default,
                    borderRadius: coach ? t.r : borders.radius.lg,
                    direction: "rtl",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    boxShadow: coach ? t.sh : shadows.card,
                    ...(coach && {
                        transition: "transform .18s cubic-bezier(.34,1.56,.64,1), box-shadow .18s",
                        "&:hover": {transform: "translateY(-3px)", boxShadow: t.shHover},
                    }),
                }}
                elevation={0}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        padding: { xs: coach ? "16px 18px" : "12px 16px", md: coach ? "18px 22px" : "20px 24px" },
                        backgroundColor: coach ? t.bg : colors.white,
                        borderBottom: coach ? `1px solid ${t.border}` : borders.default
                    }}
                >
                    {/* Title row with navigation arrow */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: { xs: "100%", md: "auto" },
                        ...(onNavigate && { flex: { md: 1 } }),
                        minWidth: 0
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: coach ? 1.5 : 1}}>
                            {Icon && (coach ? (
                                <Box sx={{
                                    width: 40, height: 40, borderRadius: "11px", flexShrink: 0,
                                    background: t.white, color: t.t2,
                                    border: `1px solid ${t.border}`,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    <Icon sx={{fontSize: 22}}/>
                                </Box>
                            ) : (
                                <Icon sx={{color: colors.textSecondary}} />
                            ))}
                            <Box sx={{minWidth: 0}}>
                                <Box sx={{
                                    fontSize: { xs: "16px", md: coach ? "17px" : "18px" },
                                    fontWeight: coach ? 800 : 600,
                                    color: coach ? t.text : colors.textPrimary,
                                    whiteSpace: "nowrap",
                                    letterSpacing: coach ? "-0.2px" : 0,
                                }}>
                                    {title}
                                </Box>
                                {coach && subtitle && (
                                    <Box sx={{fontSize: "12px", fontWeight: 500, color: t.t2, whiteSpace: "nowrap"}}>
                                        {subtitle}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        {onNavigate && (
                            <Tooltip title={navigationTooltip}>
                                <IconButton
                                    onClick={onNavigate}
                                    sx={{
                                        color: coach ? t.t2 : colors.textSecondary,
                                        "&:hover": coach
                                            ? {backgroundColor: t.greenLight, color: t.green}
                                            : {backgroundColor: colors.bgHover, color: colors.textPrimary}
                                    }}
                                    size="small"
                                >
                                    <ArrowIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    {(actionButton || headerAction) && (
                        <Box sx={{display: "flex", gap: coach ? 1.25 : 1, alignItems: "center", flexWrap: "wrap", justifyContent: {xs: "flex-start", md: "flex-end"}}}>
                            {actionButton && (
                                <Box sx={{display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap"}}>
                                    {actionButton}
                                </Box>
                            )}
                            {coach && actionButton && headerAction && (
                                <Box sx={{width: "1px", height: 22, background: t.border, flexShrink: 0}}/>
                            )}
                            {headerAction}
                        </Box>
                    )}
                </Box>

                {/* Content */}
                <Box sx={{
                    padding: { xs: "16px 12px", md: contentPadding },
                    flex: 1,
                    display: "flex",
                    flexDirection: "column"
                }}>
                    {loading && (
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300}}>
                            <CircularProgress sx={{color: coach ? t.green : colors.primary}}/>
                        </Box>
                    )}

                    {error && !loading && (
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: coach ? t.red : colors.error, fontSize: '16px'}}>
                            {error}
                        </Box>
                    )}

                    {!loading && !error && !showData && (
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: coach ? t.t3 : colors.textSecondary, fontSize: '16px'}}>
                            {emptyMessage}
                        </Box>
                    )}

                    {!loading && !error && showData && children}
                </Box>
            </Paper>
        </Box>
    );
};

export default ChartCard;
